import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  ABOUT_SECTION_KEYS,
  fetchAboutPage,
  findSection,
} from "../services/contentApi";

const AboutPageContext = createContext(null);

export function AboutPageProvider({ children }) {
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    fetchAboutPage()
      .then((data) => {
        if (!cancelled) {
          setPage(data);
          setError(null);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err);
          setPage(null);
        }
        console.error("Error fetching about page:", err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const sections = useMemo(() => {
    const result = {};
    ABOUT_SECTION_KEYS.forEach((key) => {
      result[key] = findSection(page, key);
    });
    return result;
  }, [page]);

  const value = useMemo(
    () => ({ page, sections, loading, error }),
    [page, sections, loading, error]
  );

  return (
    <AboutPageContext.Provider value={value}>
      {children}
    </AboutPageContext.Provider>
  );
}

export function useAboutPage() {
  const ctx = useContext(AboutPageContext);
  if (!ctx) {
    throw new Error("useAboutPage must be used within AboutPageProvider");
  }
  return ctx;
}

export function useAboutSection(key) {
  const ctx = useContext(AboutPageContext);
  if (!ctx) {
    return { section: null, loading: false, fromAboutPage: false };
  }
  return {
    section: ctx.sections[key] ?? null,
    loading: ctx.loading,
    fromAboutPage: true,
  };
}
